import { ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, Columns, Download } from 'lucide-preact'
import { useState, useMemo, useEffect } from 'preact/hooks'
import { useTableSort, sortData, type SortDirection } from '@/hooks/useTableSort'
import { ColumnSelectorModal, type ColumnSelectorItem } from '@/components/modals/ColumnSelectorModal'

export interface ColumnConfig<T> {
  /** Unique key for the column */
  key: string
  /** Display label for column header */
  header: string
  /** Whether this column is sortable */
  sortable?: boolean
  /** Custom width for the column (CSS value) */
  width?: string
  /** Custom renderer for cell content */
  render?: (item: T, index: number) => preact.ComponentChild
  /** Accessor function to get raw value for sorting */
  accessor?: (item: T) => string | number | boolean | Date | null | undefined
  /** Alignment for cell content */
  align?: 'left' | 'center' | 'right'
  /** Custom class name for cell */
  className?: string
  /** Responsive visibility classes (e.g., "hidden md:table-cell" for hide on mobile, show on desktop) */
  hidden?: string
  /** Column can't be hidden when column visibility is enabled (always visible) */
  required?: boolean
  /** Whether visible by default when column visibility is enabled. Default: true */
  defaultVisible?: boolean
}

export interface ExportColumnDef<T> {
  /** Header label in the CSV */
  header: string
  /** Extract the value for this column from a data item */
  getValue: (item: T) => string | number | boolean | null | undefined
}

export interface DataGridExportConfig<T> {
  /** Function to generate the filename (called at export time, should include .csv extension) */
  getFilename: () => string
  /** Column definitions for the CSV export */
  columns: ExportColumnDef<T>[]
}

export interface DataGridProps<T> {
  /** Array of data items to display */
  data: T[]
  /** Column configuration */
  columns: ColumnConfig<T>[]
  /** Unique key function to identify rows */
  getRowKey: (item: T) => string | number
  /** Whether pagination is enabled */
  pagination?: boolean
  /** Number of items per page (default: 10) */
  pageSize?: number
  /** Loading state */
  isLoading?: boolean
  /** Error message to display */
  error?: string
  /** Empty state message */
  emptyMessage?: string
  /** Click handler for rows */
  onRowClick?: (item: T) => void
  /** Custom comparator function for sorting */
  getSortComparator?: (key: string) => (a: T, b: T) => number
  /** Default sort key */
  defaultSortKey?: string
  /** Default sort direction */
  defaultSortDirection?: SortDirection
  /** Custom class name for container */
  className?: string
  /** Show borders between rows */
  showRowBorders?: boolean
  /** When true, columns maintain natural widths and the table scrolls horizontally instead of squishing */
  scrollable?: boolean
  /** localStorage key for column visibility. When set, enables column visibility toggle with persistence */
  columnVisibilityKey?: string
  /** Export configuration. When set, enables an Export button in the toolbar that generates a CSV download */
  exportConfig?: DataGridExportConfig<T>
  /**
   * Whatever the caller narrowed `data` by — a search query, a filter selection. Changing it sends
   * the reader back to the first page.
   *
   * Row count alone cannot decide this: two different queries can match the same number of rows,
   * and holding page 3 across that change shows results whose first page nobody has seen. Only the
   * caller knows the set was rebuilt rather than merely changed size.
   */
  pageResetKey?: string | number
}

/** Escape a CSV value (wrap in quotes if it contains commas, quotes, or newlines) */
function escapeCsvValue(value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined) return ''
  const str = String(value)
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

/** Generate CSV string from data and export column definitions */
function generateCsv<T>(data: T[], columns: ExportColumnDef<T>[]): string {
  const headers = columns.map(col => col.header)
  const rows = data.map(item =>
    columns.map(col => escapeCsvValue(col.getValue(item)))
  )
  return [headers.join(','), ...rows.map(row => row.join(','))].join('\n')
}

/** Trigger a browser download of CSV content */
function downloadCsv(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.setAttribute('rel', 'noopener noreferrer')
  link.setAttribute('target', '_self')
  link.dispatchEvent(new MouseEvent('click', { view: window, bubbles: false, cancelable: true }))
  setTimeout(() => URL.revokeObjectURL(url), 100)
}

/** Hook for managing column visibility state with localStorage persistence */
function useColumnVisibility<T>(
  columns: ColumnConfig<T>[],
  storageKey: string | undefined
) {
  const [visibilityMap, setVisibilityMap] = useState<Record<string, boolean>>(() => {
    if (!storageKey) return {}
    try {
      const stored = localStorage.getItem(storageKey)
      if (stored) return JSON.parse(stored) as Record<string, boolean>
    } catch {
      // ignore parse errors
    }
    return {}
  })

  const isVisible = (key: string): boolean => {
    if (key in visibilityMap) return visibilityMap[key]
    const col = columns.find(c => c.key === key)
    return col?.defaultVisible !== false
  }

  const toggleColumn = (key: string) => {
    const col = columns.find(c => c.key === key)
    if (col?.required) return
    const next = { ...visibilityMap, [key]: !isVisible(key) }
    setVisibilityMap(next)
    if (storageKey) {
      try { localStorage.setItem(storageKey, JSON.stringify(next)) } catch { /* ignore */ }
    }
  }

  const resetToDefaults = () => {
    setVisibilityMap({})
    if (storageKey) {
      try { localStorage.removeItem(storageKey) } catch { /* ignore */ }
    }
  }

  const visibleColumns = storageKey
    ? columns.filter(col => isVisible(col.key))
    : columns

  const selectorItems: ColumnSelectorItem[] = columns.map(col => ({
    id: col.key,
    label: col.header,
    visible: isVisible(col.key),
    required: col.required,
  }))

  return { visibleColumns, selectorItems, toggleColumn, resetToDefaults }
}

export function DataGrid<T>({
  data,
  columns,
  getRowKey,
  pagination = false,
  pageSize = 10,
  isLoading = false,
  error,
  emptyMessage = 'No data available',
  onRowClick,
  getSortComparator,
  defaultSortKey,
  defaultSortDirection = 'asc',
  className = '',
  showRowBorders = true,
  scrollable = false,
  columnVisibilityKey,
  exportConfig,
  pageResetKey,
}: DataGridProps<T>) {
  const [page, setPage] = useState(1)
  const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false)
  const { sortConfig, requestSort, getSortIndicator } = useTableSort<string>(
    defaultSortKey,
    defaultSortDirection
  )
  const { visibleColumns, selectorItems, toggleColumn, resetToDefaults } = useColumnVisibility(columns, columnVisibilityKey)

  // Apply sorting
  const sortedData = useMemo(() => {
    if (!getSortComparator || !sortConfig) {
      return data
    }
    return sortData(data, sortConfig, getSortComparator)
  }, [data, sortConfig, getSortComparator])

  // The page actually rendered, clamped rather than stored. Callers filter before handing `data`
  // over, so the set can shrink under the current page between renders; clamping lands on a page
  // that exists instead of on an empty one. Correcting it afterwards with a state write would mean
  // catching every path that can shrink the data, and the one that gets missed shows a populated
  // grid with no rows in it.
  const totalPages = pagination ? Math.max(1, Math.ceil(sortedData.length / pageSize)) : 1
  const currentPage = Math.min(page, totalPages)

  const paginatedData = useMemo(() => {
    if (!pagination) {
      return sortedData
    }
    const start = (currentPage - 1) * pageSize
    return sortedData.slice(start, start + pageSize)
  }, [sortedData, currentPage, pageSize, pagination])

  // A different set of rows is a different list, and page 3 of it is not where anyone means to
  // land. Row count catches most of it; `pageResetKey` catches the rest, where a caller's new
  // filter happens to match the same number of rows as the old one.
  useEffect(() => {
    setPage(1)
  }, [data.length, pageResetKey])

  const tableClass = `${scrollable ? 'w-max min-w-full' : 'min-w-full'} divide-y divide-slate-200 dark:divide-slate-700`

  // Render sort indicator
  const renderSortIcon = (columnKey: string) => {
    const direction = getSortIndicator(columnKey)
    if (direction === 'asc') {
      return <ArrowUp size={14} className="text-primary-600 dark:text-primary-400" />
    }
    if (direction === 'desc') {
      return <ArrowDown size={14} className="text-primary-600 dark:text-primary-400" />
    }
    return <ArrowUpDown size={14} className="text-slate-400 dark:text-slate-500" />
  }

  // Row keyboard handler for accessibility
  const handleRowKeyDown = (e: KeyboardEvent, item: T) => {
    if (onRowClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault()
      onRowClick(item)
    }
  }

  // Export handler
  const handleExport = exportConfig ? () => {
    if (sortedData.length === 0) return
    const csv = generateCsv(sortedData, exportConfig.columns)
    downloadCsv(csv, exportConfig.getFilename())
  } : undefined

  // Toolbar (shown when column visibility or export is enabled)
  const hasToolbar = columnVisibilityKey || exportConfig
  const columnToolbar = hasToolbar ? (
    <div className="flex items-center justify-end gap-2 px-4 py-2 border-b border-slate-200 dark:border-slate-700">
      {exportConfig && (
        <button
          onClick={handleExport}
          disabled={sortedData.length === 0}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Export to CSV"
        >
          <Download size={14} />
          Export
        </button>
      )}
      {columnVisibilityKey && (
        <button
          onClick={() => setIsColumnSelectorOpen(true)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
          title="Select Columns"
        >
          <Columns size={14} />
          Columns
        </button>
      )}
    </div>
  ) : null

  // Column selector modal
  const columnSelectorModal = columnVisibilityKey ? (
    <ColumnSelectorModal
      isOpen={isColumnSelectorOpen}
      onClose={() => setIsColumnSelectorOpen(false)}
      columns={selectorItems}
      onToggle={toggleColumn}
      onReset={resetToDefaults}
    />
  ) : null

  // Render loading state
  if (isLoading) {
    return (
      <>
        <div className={`bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg ${className}`}>
          {columnToolbar}
          <div className="overflow-x-auto">
            <table className={tableClass}>
              <thead className="bg-slate-50 dark:bg-slate-900">
                <tr>
                  {visibleColumns.map((col) => (
                    <th
                      key={col.key}
                      style={{ width: col.width }}
                      className={`px-4 py-3 text-${col.align || 'left'} text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider`}
                    >
                      {col.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan={visibleColumns.length} className="px-4 py-12 text-center">
                    <div className="flex items-center justify-center gap-2 text-slate-500 dark:text-slate-400">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-600"></div>
                      <span>Loading...</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        {columnSelectorModal}
      </>
    )
  }

  // Render error state
  if (error) {
    return (
      <>
        <div className={`bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg ${className}`}>
          {columnToolbar}
          <div className="px-4 py-12 text-center">
            <div className="text-red-600 dark:text-red-400 mb-2">Error</div>
            <div className="text-slate-600 dark:text-slate-400">{error}</div>
          </div>
        </div>
        {columnSelectorModal}
      </>
    )
  }

  // Render empty state
  if (data.length === 0) {
    return (
      <>
        <div className={`bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg ${className}`}>
          {columnToolbar}
          <div className="overflow-x-auto">
            <table className={tableClass}>
              <thead className="bg-slate-50 dark:bg-slate-900">
                <tr>
                  {visibleColumns.map((col) => (
                    <th
                      key={col.key}
                      style={{ width: col.width }}
                      className={`px-4 py-3 text-${col.align || 'left'} text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider`}
                    >
                      {col.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan={visibleColumns.length} className="px-4 py-12 text-center text-slate-500 dark:text-slate-400">
                    {emptyMessage}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        {columnSelectorModal}
      </>
    )
  }

  return (
    <>
      <div className={className}>
        {/* Table */}
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
          {columnToolbar}
          <div className="overflow-x-auto">
            <table className={tableClass}>
              <thead className="bg-slate-50 dark:bg-slate-900">
                <tr>
                  {visibleColumns.map((col) => (
                    <th
                      key={col.key}
                      style={{ width: col.width }}
                      className={`px-4 py-3 text-${col.align || 'left'} text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider ${
                        col.sortable ? 'cursor-pointer select-none hover:bg-slate-100 dark:hover:bg-slate-800' : ''
                      } ${col.hidden || ''}`}
                      onClick={() => col.sortable && requestSort(col.key)}
                    >
                      <div className="flex items-center gap-2">
                        <span>{col.header}</span>
                        {col.sortable && renderSortIcon(col.key)}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className={showRowBorders ? 'divide-y divide-slate-200 dark:divide-slate-700' : ''}>
                {paginatedData.map((item, index) => (
                  <tr
                    key={getRowKey(item)}
                    tabIndex={onRowClick ? 0 : undefined}
                    className={
                      onRowClick
                        ? 'hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500'
                        : ''
                    }
                    onClick={() => onRowClick?.(item)}
                    onKeyDown={(e) => handleRowKeyDown(e, item)}
                  >
                    {visibleColumns.map((col) => (
                      <td
                        key={col.key}
                        className={`px-4 py-3 text-sm text-slate-900 dark:text-slate-100 ${col.className || ''} ${col.hidden || ''}`}
                        style={{ textAlign: col.align || 'left' }}
                      >
                        {col.render ? col.render(item, index) : col.accessor ? col.accessor(item) : null}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {pagination && totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 mt-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg">
            <div className="text-sm text-slate-600 dark:text-slate-400">
              Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, sortedData.length)} of{' '}
              {sortedData.length} items
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-md hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={16} />
                Previous
              </button>
              <span className="text-sm text-slate-600 dark:text-slate-400">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-md hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
      {columnSelectorModal}
    </>
  )
}
